import { useEffect, useRef, useState } from 'react';

const MAP_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
const CENTER = { lat: 32.805, lng: -79.91 };
const ZOOM = 12;

// Mute the default Google palette toward the page's cream/navy so the map
// reads as part of the almanac instead of a pasted widget; the traffic layer's
// green/amber/red still pops against the quiet base. Classic `styles` applies
// to raster maps (no mapId), which is what we create below.
const ALMANAC_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f6efe2' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#3f6694' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#fdfaf3' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bccde2' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#30517a' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e8dcc4' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f1e2c0' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#dab064' }] }
];
const EXTERNAL_URL =
  'https://www.google.com/maps/dir/?api=1&origin=Mount+Pleasant,SC&destination=Downtown+Charleston,SC&travelmode=driving&layer=traffic';

let mapsBootstrapped = false;
function bootstrapGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return;
  // Both failure hooks go up before the script is fetched, never earlier.
  if (!window.gm_authFailure) window.gm_authFailure = reportMapsFailure;
  watchMapsConsole();
  if (mapsBootstrapped) return;
  if (window.google?.maps?.importLibrary) {
    mapsBootstrapped = true;
    return;
  }
  ((g) => {
    var h, a, k, p = 'The Google Maps JavaScript API',
        c = 'google', l = 'importLibrary', q = '__ib__',
        m = document, b = window;
    b = b[c] || (b[c] = {});
    var d = b.maps || (b.maps = {}),
        r = new Set(),
        e = new URLSearchParams(),
        u = () => h || (h = new Promise(async (f, n) => {
          await (a = m.createElement('script'));
          e.set('libraries', [...r] + '');
          for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => (h = n(Error(p + ' could not load.')));
          a.nonce = m.querySelector('script[nonce]')?.nonce || '';
          m.head.append(a);
        }));
    if (d[l]) return;
    d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
  })({ key: apiKey, v: 'weekly' });
  mapsBootstrapped = true;
}

// Google reports a bad key, a blocked referrer, or a project without billing
// asynchronously through this global rather than by throwing: the Map object
// builds, we set ready, and Google then paints "For development purposes only"
// across the tiles under its own "This page can't load Google Maps correctly"
// dialog. That is what a guest saw on 2026-09-21 (BillingNotEnabledMapError).
// The keyless embed below needs no key and no billing, so we switch to it.
let mapsAuthFailed = false;
const authListeners = new Set();
function reportMapsFailure() {
  mapsAuthFailed = true;
  authListeners.forEach((fn) => fn());
}

// gm_authFailure alone is not enough: Google does NOT call it for
// BillingNotEnabledMapError (checked against the live key on 2026-09-21, where
// the callback was installed before the script and never ran). What every
// error of this class does emit is one console line beginning "Google Maps
// JavaScript API error", so we listen for that too, pass it through untouched,
// and unpatch as soon as it fires or once the map has had time to settle.
let consoleWatched = false;
function watchMapsConsole() {
  if (consoleWatched || typeof console === 'undefined') return;
  consoleWatched = true;
  const original = console.error;
  const patched = (...args) => {
    original.apply(console, args);
    if (args.some((a) => String(a).includes('Google Maps JavaScript API error'))) {
      if (console.error === patched) console.error = original;
      reportMapsFailure();
    }
  };
  console.error = patched;
  setTimeout(() => {
    if (console.error === patched) console.error = original;
  }, 20000);
}

// Subscribe only. The hooks themselves are armed in bootstrapGoogleMaps, which
// runs immediately before the script is fetched: the map is lazy, so a guest
// can reach it long after this component mounted, and a listening window that
// started on mount would already have closed.
function watchMapsAuth(onFail) {
  authListeners.add(onFail);
  if (mapsAuthFailed) onFail();
  return () => authListeners.delete(onFail);
}

// `keyed` is true when a key exists but Google refused it. The env-var hint is
// for a developer with no key at all; a guest must never be told to set one.
function FallbackMap({ keyed = false }) {
  const fallbackSrc = `https://maps.google.com/maps?q=Ravenel+Bridge+Charleston+SC&t=m&z=${ZOOM}&output=embed&layer=t`;
  // Frames sit on cream-50, not white: the map is the content, and white is
  // for inputs only (DECISIONS 2026-09-20). Same on the live and lazy frames.
  // No drop shadow: nothing else on the paper floats (design review
  // 2026-09-26, finding 5).
  return (
    <div className="overflow-hidden rounded-2xl border border-navy-100 bg-cream-50">
      <div className="aspect-[16/10] w-full sm:aspect-[16/9]">
        <iframe
          src={fallbackSrc}
          title="Charleston / Mount Pleasant map"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-full w-full"
        />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-navy-100 bg-cream-50 px-3 py-2 text-xs">
        <span className="text-navy-500">
          {keyed ? (
            'Live traffic, Google Maps'
          ) : (
            <>
              Set <code className="rounded bg-cream-100 px-1 text-[11px]">VITE_GOOGLE_MAPS_KEY</code> for the live traffic layer
            </>
          )}
        </span>
        <a
          href={EXTERNAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-navy-700 underline-offset-2 hover:underline"
        >
          Open in Maps
        </a>
      </div>
    </div>
  );
}

// Homepage perf: the Maps JS API (or the fallback iframe) is the heaviest
// asset on the page and sits below the fold for every persona. Nothing
// map-related loads until the section is within ~400px of the viewport.
export default function TrafficMap() {
  const hostRef = useRef(null);
  const [near, setNear] = useState(false);
  const [mapsBlocked, setMapsBlocked] = useState(mapsAuthFailed);

  // Armed on mount, before the lazy loader ever fetches the Maps script.
  useEffect(() => {
    if (!MAP_KEY) return undefined;
    return watchMapsAuth(() => setMapsBlocked(true));
  }, []);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);

  return (
    <div ref={hostRef}>
      {near ? (
        MAP_KEY && !mapsBlocked ? (
          <LiveMap />
        ) : (
          <FallbackMap keyed={Boolean(MAP_KEY)} />
        )
      ) : (
        <div className="overflow-hidden rounded-2xl border border-navy-100 bg-cream-50">
          <div className="flex aspect-[16/10] w-full items-center justify-center bg-cream-50 text-xs text-navy-500 sm:aspect-[16/9]">
            Map loads as you scroll
          </div>
        </div>
      )}
    </div>
  );
}

function LiveMap() {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!MAP_KEY) return;
    let cancelled = false;
    (async () => {
      try {
        bootstrapGoogleMaps(MAP_KEY);
        const { Map, TrafficLayer } = await window.google.maps.importLibrary('maps');
        if (cancelled || !containerRef.current) return;
        const map = new Map(containerRef.current, {
          center: CENTER,
          zoom: ZOOM,
          mapTypeId: 'roadmap',
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          gestureHandling: 'cooperative',
          keyboardShortcuts: false,
          clickableIcons: false,
          styles: ALMANAC_MAP_STYLES
        });
        const trafficLayer = new TrafficLayer();
        trafficLayer.setMap(map);
        setReady(true);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="overflow-hidden rounded-2xl border border-navy-100 bg-cream-50">
      <div className="relative aspect-[16/10] w-full sm:aspect-[16/9]">
        <div ref={containerRef} className="absolute inset-0" />
        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-cream-50 text-xs text-navy-500">
            Loading live traffic…
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-navy-100 bg-cream-50 px-3 py-2 text-xs">
        <span className="text-navy-500">
          {error ? `Map failed to load: ${error}` : 'Live traffic, Google Maps'}
        </span>
        <a
          href={EXTERNAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-navy-700 underline-offset-2 hover:underline"
        >
          Open directions
        </a>
      </div>
    </div>
  );
}
